import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';

import styles from './Drawer.module.css';

import { Backdrop } from 'components/Backdrop/Backdrop';
import { OverlayCloseButton } from 'components/OverlayCloseButton';

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className'> {
  /** The buttons to render in the drawer. */
  buttons?: ReactNode;
  /** true if the drawer should be open; otherwise, false. The default is false. */
  isOpen?: boolean;
  /** The callback method that is invoked when the drawer is closed. */
  onClose: () => void;
  /** The title of the drawer. */
  title: string;
  /** The aria-label of the close button. */
  closeButtonAriaValueText?: string;
}

const closingDelay = 200;

/** Represents a Drawer component. */
export function Drawer({
  buttons,
  children,
  isOpen,
  onClose,
  title,
  closeButtonAriaValueText = 'Close',
  ...restProps
}: PropsWithChildren<DrawerProps>) {
  const [containerClassNames, setContainerClassNames] = useState(styles.drawer);
  // Hide content outside the modal from screen readers.
  const { modalProps } = useModal({ isDisabled: !isOpen });
  const contentRef = useRef<HTMLDivElement>(null);

  function onCloseDrawer() {
    setContainerClassNames(styles.drawer);

    globalThis.setTimeout(() => {
      onClose();
    }, closingDelay);
  }

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const { overlayProps, underlayProps } = useOverlay(
    { isDismissable: true, isKeyboardDismissDisabled: false, isOpen, onClose: onCloseDrawer },
    contentRef
  );

  // Get props for the dialog and its title
  const { dialogProps, titleProps } = useDialog({}, contentRef);

  usePreventScroll({ isDisabled: !isOpen });

  useEffect(() => {
    // setting class name after render so that element can slide in after it is mounted and slide out
    // before it is unmounted
    setContainerClassNames(clsx(styles.drawer, { [styles['drawer--open']]: isOpen }));
  }, [isOpen]);

  return (
    <OverlayContainer>
      <Backdrop {...underlayProps}>
        <FocusScope contain restoreFocus>
          <aside
            {...modalProps}
            {...overlayProps}
            {...dialogProps}
            {...restProps}
            className={containerClassNames}
            ref={contentRef}
          >
            <header className={styles.drawer__header}>
              <h1 className={styles.drawer__heading} {...titleProps}>
                {title}
              </h1>

              <OverlayCloseButton aria-label={closeButtonAriaValueText} onClick={onCloseDrawer} />
            </header>

            <div className={styles.drawer__content}>{children}</div>

            {buttons && (
              <footer className={styles.drawer__footer}>
                <div className={styles.drawer__buttons}>{buttons}</div>
              </footer>
            )}
          </aside>
        </FocusScope>
      </Backdrop>
    </OverlayContainer>
  );
}
